/**
 * odist 서브계정 권한 헬퍼 (2026-08-24)
 *
 * 서브계정이 아니면(마스터 본인·매장 본인) 전부 허용. 서브계정이면 sub_permissions 스냅샷을 본다.
 *   sub_permissions = {kind:'odist', role_name, pages:{key:'read'|'write'}, options:{...}, scope:{master, stores}}
 *
 * 사용처: 라우터 가드(페이지 진입), App.vue 메뉴, 화면 안 수정 버튼(v-perm-write / $odistCan)
 */
import { odistMenuItemByPath, odistMenuItem, ODIST_MENUS, odistItemVisibleFor, ODIST_MASTER_ADMIN_PATHS } from '@/config/odist-menus'

/** odist 서브계정 스냅샷. 아니면 null */
export function odistSubPerm(store) {
  const st = store && store.state ? store.state : store
  if (!st || !st.is_subuser) return null
  const p = st.sub_permissions
  if (!p || p.kind !== 'odist') return null
  return p
}

export function odistLevel(store, key) {
  const p = odistSubPerm(store)
  if (!p) return 'write'
  const v = p.pages && p.pages[key]
  return v === 'write' || v === 'read' ? v : 'none'
}

export function odistCanRead(store, key) {
  return odistLevel(store, key) !== 'none'
}

export function odistCanWrite(store, key) {
  return odistLevel(store, key) === 'write'
}

/**
 * '내 매장 / 전체 통합'을 고르는 화면의 기본값을 '전체'로 할 계정인가(2026-10-22 운영자 "권한 있는 계정이면 기본값을 전체로").
 * 본사·매장 계정 = 전체를 볼 수 있으니 true. 직원 계정 = 범위가 본사(master)이거나 매장 2곳 이상일 때만 true —
 * 한 매장만 맡은 직원은 지금처럼 '내 매장'으로 연다(고르면 전체도 볼 수 있는 건 그대로).
 */
export function odistDefaultAll(store) {
  const p = odistSubPerm(store)
  if (!p) return true
  const sc = p.scope || {}
  return !!sc.master || (Array.isArray(sc.stores) && sc.stores.length > 1)
}

/** 역할 옵션 (매입단가 보기 등). 서브계정이 아니면 true */
export function odistOption(store, key) {
  const p = odistSubPerm(store)
  if (!p) return true
  const o = p.options || {}
  return o[key] !== false
}

/**
 * 경로 진입 허용 여부. 서브계정이 아니면 항상 허용.
 * 반환: { allowed: bool, item, level }
 */
export function odistPathAccess(store, fullPath) {
  const p = odistSubPerm(store)
  if (!p) return { allowed: true, item: null, level: 'write' }
  const path = String(fullPath || '').split('?')[0]
  if (ODIST_MASTER_ADMIN_PATHS.some(m => path === m || path.startsWith(m + '/'))) {
    return { allowed: false, item: null, level: 'none' }
  }
  if (path === '/admin/odist' || path === '/admin/odist/') return { allowed: true, item: null, level: 'read' }
  const item = odistMenuItemByPath(path)
  if (!item) {
    // odist 밖의 경로(/admin/erp 등)는 odist 서브계정에게 전부 막는다
    return { allowed: false, item: null, level: 'none' }
  }
  const level = odistLevel(store, item.key)
  return { allowed: level !== 'none', item, level }
}

/** 현재 계정 종류(마스터/매장)에서 첫 번째로 열 수 있는 메뉴 경로 */
export function odistFirstAllowedPath(store, isMaster) {
  const p = odistSubPerm(store)
  for (const g of ODIST_MENUS) {
    for (const it of g.items) {
      const item = odistMenuItem(it.key)
      if (!odistItemVisibleFor(item, isMaster)) continue
      if (it.subOnly && !p) continue   // 서브계정 전용 항목은 마스터 본인의 랜딩 후보에서 제외
      if (!p || odistCanRead(store, it.key)) return it.path
    }
  }
  return null
}

/** v-perm-write="'order'" — 쓰기 권한이 없으면 요소를 숨긴다(disabled 가 아니라 제거에 가깝게) */
export const permWriteDirective = (store) => ({
  mounted(el, binding) { apply(el, binding, store) },
  updated(el, binding) { apply(el, binding, store) },
})

function apply(el, binding, store) {
  const key = binding.value
  const ok = !key || odistCanWrite(store, key)
  if (ok) {
    if (el.__permHidden) { el.style.display = el.__permDisplay || ''; el.__permHidden = false }
  } else {
    if (!el.__permHidden) { el.__permDisplay = el.style.display; el.__permHidden = true }
    el.style.display = 'none'
  }
}
